import { getCookie, removeCookie, setCookie } from "../../CookieHelper";
import { COOKIE_ENUM } from "../../enums";

/**
 * Where a "session" starts, in the customer's local hours.
 *
 * Not midnight: someone ordering at 11pm and someone ordering at 5am the next
 * morning are in the same trading night, and the story asks for those to count
 * as one session. Shifting the clock back before taking the date makes that fall
 * out -- 23:00 and 05:00 land on the same key, 07:00 starts a new one.
 *
 * The backend recomputes this authoritatively in the *restaurant's* timezone.
 * This copy exists only to avoid pointless requests, so an approximation using
 * the browser's own clock is fine -- at worst we send one extra call a day.
 */
const BUSINESS_DAY_CUTOFF_HOUR = 6;

/**
 * `YYYY-MM-DD` built from **local** date parts.
 *
 * Deliberately not `toISOString().slice(0, 10)`: that returns the UTC day, which
 * is a different date for most of the world for part of every day, and would
 * misbehave only for customers ordering near midnight -- the exact bug that
 * would survive review.
 */
export const getBusinessDayKey = (
  now: Date = new Date(),
  cutoffHour: number = BUSINESS_DAY_CUTOFF_HOUR
): string => {
  const shifted = new Date(now.getTime() - cutoffHour * 60 * 60 * 1000);

  const month = `${shifted.getMonth() + 1}`.padStart(2, "0");
  const day = `${shifted.getDate()}`.padStart(2, "0");

  return `${shifted.getFullYear()}-${month}-${day}`;
};

/** True when this browser has not yet reported the current session. */
export const shouldSendMetaSession = (now: Date = new Date()): boolean => {
  try {
    return (
      getCookie(COOKIE_ENUM.META_SESSION_LAST_SENT) !== getBusinessDayKey(now)
    );
  } catch {
    // Cookies unavailable — send, and let the backend's own upsert de-duplicate.
    return true;
  }
};

/**
 * Forgets that this session was reported, so the next page view reports again.
 *
 * Called on every transition between visitor and customer -- login, sign out and
 * account deletion. The identifiers are stored against whichever customer id was
 * current when they were sent, so without this the id on the other side of the
 * transition would get no row until tomorrow, and the hourly Purchase and
 * InitiateCheckout jobs would find nothing to attach.
 */
export const clearMetaSessionGuard = (): void => {
  try {
    removeCookie(COOKIE_ENUM.META_SESSION_LAST_SENT);
  } catch {
    // Worst case the guard stands and we report tomorrow instead.
  }
};

export const markMetaSessionSent = (now: Date = new Date()): void => {
  try {
    setCookie(COOKIE_ENUM.META_SESSION_LAST_SENT, getBusinessDayKey(now));
  } catch {
    // Not being able to remember means we retry next page load. Harmless.
  }
};
